// Structured clarifying questions for the unattended "Coding Assistant" agent
// (see docs / mcp-server.ts `ask_questions`). The agent posts an ordinary task
// comment whose body ends in a fenced `taskei-questions` JSON code block; the
// task drawer renders that block as an answerable form -- but ONLY on tasks
// assigned to the Coding Assistant. Everywhere else it is just a comment, and
// the human-readable list above the block keeps it legible.
//
// Dependency-free (no React/DOM) so the Worker, the UI and tests share it.

import { docToPlainText, type TiptapDoc, type TiptapNode } from "./rich-text-doc";

/** Synthetic service user created by migrations/0062_coding_assistant_service_user.sql. */
export const CODING_ASSISTANT_USER_ID = "user-coding-assistant";
/** Every agent comment starts with this prefix (agent tokens post as a user). */
export const AGENT_MARKER = "[claude-agent]";
export const QUESTIONS_LANGUAGE = "taskei-questions";

export const MAX_QUESTIONS = 4;
export const MAX_OPTIONS = 4;

export type AgentQuestionOption = { description?: string; label: string };
export type AgentQuestion = {
  header?: string;
  id: string;
  multiSelect?: boolean;
  options?: AgentQuestionOption[];
  question: string;
};
/** What the person picked for one question. `other` is free text. */
export type AgentAnswer = { other?: string; questionId: string; selected: string[] };

const clip = (value: unknown, max: number): string =>
  typeof value === "string" ? value.trim().slice(0, max) : "";

/**
 * Validates untrusted question input (from the MCP tool or a stored comment)
 * and returns a clean list, or an error string. Option-less questions are free
 * text; with options the UI also always offers an "Other" free-text escape.
 */
export const normalizeQuestions = (raw: unknown): { error: string } | { questions: AgentQuestion[] } => {
  if (!Array.isArray(raw) || raw.length === 0) return { error: "questions must be a non-empty array." };
  if (raw.length > MAX_QUESTIONS) return { error: `Ask at most ${MAX_QUESTIONS} questions.` };
  const questions: AgentQuestion[] = [];
  for (const [index, item] of raw.entries()) {
    if (!item || typeof item !== "object") return { error: `Question ${index + 1} must be an object.` };
    const entry = item as Record<string, unknown>;
    const question = clip(entry.question, 300);
    if (!question) return { error: `Question ${index + 1} needs a "question" string.` };
    let options: AgentQuestionOption[] | undefined;
    if (entry.options !== undefined) {
      if (!Array.isArray(entry.options) || entry.options.length < 2 || entry.options.length > MAX_OPTIONS) {
        return { error: `Question ${index + 1}: options must have 2-${MAX_OPTIONS} entries (or be omitted for free text).` };
      }
      options = [];
      for (const option of entry.options) {
        const label = clip((option as Record<string, unknown> | null)?.label, 80);
        if (!label) return { error: `Question ${index + 1}: every option needs a "label".` };
        const description = clip((option as Record<string, unknown>).description, 200);
        options.push(description ? { description, label } : { label });
      }
    }
    const header = clip(entry.header, 24);
    questions.push({
      ...(header ? { header } : {}),
      id: clip(entry.id, 40) || `q${index + 1}`,
      ...(entry.multiSelect === true && options ? { multiSelect: true } : {}),
      ...(options ? { options } : {}),
      question,
    });
  }
  if (new Set(questions.map((question) => question.id)).size !== questions.length) {
    return { error: "Question ids must be unique." };
  }
  return { questions };
};

const textNode = (text: string, bold = false): TiptapNode =>
  bold ? { marks: [{ type: "bold" }], text, type: "text" } : { text, type: "text" };

/** The comment body the agent posts: readable list first, JSON block last. */
export const buildQuestionsDoc = (questions: AgentQuestion[], intro?: string): TiptapDoc => ({
  content: [
    {
      content: [textNode(`${AGENT_MARKER} ${clip(intro, 300) || "I need these answered before starting:"}`)],
      type: "paragraph",
    },
    ...questions.map((question, index): TiptapNode => ({
      content: [textNode(
        `${index + 1}. ${question.question}`
          + (question.options ? ` (${question.options.map((option) => option.label).join(" / ")})` : ""),
      )],
      type: "paragraph",
    })),
    {
      attrs: { language: QUESTIONS_LANGUAGE },
      content: [{ text: JSON.stringify({ questions }), type: "text" }],
      type: "codeBlock",
    },
  ],
  type: "doc",
});

/** Pulls the structured questions out of a comment body, if it has any. */
export const extractAgentQuestions = (doc: TiptapDoc | null | undefined): AgentQuestion[] | null => {
  for (const node of doc?.content ?? []) {
    if (node.type !== "codeBlock" || node.attrs?.language !== QUESTIONS_LANGUAGE) continue;
    const text = (node.content ?? []).map((child) => child.text ?? "").join("");
    try {
      const parsed = JSON.parse(text) as { questions?: unknown };
      const result = normalizeQuestions(parsed.questions);
      return "questions" in result ? result.questions : null;
    } catch {
      return null;
    }
  }
  return null;
};

export const isAgentQuestionsAnswered = (questions: AgentQuestion[], answers: AgentAnswer[]): boolean =>
  questions.every((question) => {
    const answer = answers.find((candidate) => candidate.questionId === question.id);
    return !!answer && (answer.selected.length > 0 || !!answer.other?.trim());
  });

/** The human's reply: plain, readable text the agent picks up via list_comments. */
export const buildAnswersDoc = (questions: AgentQuestion[], answers: AgentAnswer[]): TiptapDoc => ({
  content: [
    { content: [textNode("Answers:", true)], type: "paragraph" },
    ...questions.map((question, index): TiptapNode => {
      const answer = answers.find((candidate) => candidate.questionId === question.id);
      const parts = [...(answer?.selected ?? []), ...(answer?.other?.trim() ? [answer.other.trim()] : [])];
      return {
        content: [
          textNode(`${index + 1}. ${question.question}`, true),
          { type: "hardBreak" },
          textNode(parts.join("; ") || "(no answer)"),
        ],
        type: "paragraph",
      };
    }),
  ],
  type: "doc",
});

export const isAgentComment = (doc: TiptapDoc | null | undefined): boolean =>
  docToPlainText(doc).trimStart().startsWith(AGENT_MARKER);

/** For display: drops the "[claude-agent]" prefix so the comment reads naturally. */
export const stripAgentMarker = (doc: TiptapDoc): TiptapDoc => {
  const [first, ...rest] = doc.content;
  const lead = first?.content?.[0];
  if (first?.type !== "paragraph" || lead?.type !== "text" || !lead.text?.trimStart().startsWith(AGENT_MARKER)) return doc;
  const text = lead.text.trimStart().slice(AGENT_MARKER.length).trimStart();
  const content = text ? [{ ...lead, text }, ...(first.content ?? []).slice(1)] : (first.content ?? []).slice(1);
  return { content: [...(content.length ? [{ ...first, content }] : []), ...rest], type: "doc" };
};

/** Drops the machine-readable JSON block so already-answered questions read as plain text. */
export const withoutQuestionsBlock = (doc: TiptapDoc): TiptapDoc => ({
  ...doc,
  content: doc.content.filter((node) => !(node.type === "codeBlock" && node.attrs?.language === QUESTIONS_LANGUAGE)),
});
